import { useState, useEffect } from 'react';
import { Input, InputAdornment, FormControl } from '@mui/material';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import FieldNames from '../../../defaults/FieldNames.json';
import { useSelector } from 'react-redux';
import { removeLetters } from '../../../utils/FormatHelper';
import { removeNegative } from '../../../utils/FormatHelper';
import { removeDecimal } from '../../../utils/FormatHelper';
import { currencyFormat } from '../../../utils/FormatHelper';

const useStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid red',
        },
        '&&:after': {
            borderBottom: '2px solid red',
        },
    },
});

const useNotSavedStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid gold',
        },
        '&&:after': {
            borderBottom: '2px solid gold',
        },
    },
});

const theme = createTheme({
    overrides: {},
});

export default function DetailsCurrency({
    detail,
    currencyValue,
    field,
    onFieldChange,
    isRequired,
    getAllApprovalFieldsCount,
    costFields,
}) {
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.activityValidation;
    });
    const actualsTreatmentValidationUnsavedValues = useSelector((state) => {
        return state.actualsTreatmentValidation;
    });
    const actualsActivityValidationUnsavedValues = useSelector((state) => {
        return state.actualsActivityValidation;
    });

    const classes = useStyles();
    const notSavedClasses = useNotSavedStyles();

    const [value, setValue] = useState('');

    useEffect(() => {
        if (currencyValue === '') {
            setValue('');
        } else {
            if (currencyValue === null || currencyValue === undefined) {
                //Adding a zero here only populates the 0 in the field, not in the treatment/activity
                setValue('');
            } else {
                const newFormat = currencyFormat(currencyValue.toString());
                setValue(newFormat);
            }
        }
    }, [currencyValue]);

    const getMargin = () => {
        if (
            field === FieldNames.featureFields.estimatedTotalCost ||
            field === FieldNames.featureFields.estimatedAssetCost ||
            field === FieldNames.featureFields.estimatedContractualCost ||
            field === FieldNames.featureFields.estimatedGrantsFixedCost ||
            field === FieldNames.featureFields.estimatedOtherCost ||
            field === FieldNames.featureFields.estimatedPersonnelCost
        ) {
            return 0;
        } else {
            return 12;
        }
    };

    const inputValueMissing = (value) => {
        if (value) {
            const numberVal = parseInt(value.toString().replaceAll(',', ''));
            if (
                costFields &&
                costFields.includes(field) &&
                field !== FieldNames.featureFields.estimatedTotalCost
            ) {
                if (
                    !isNaN(numberVal) &&
                    numberVal !== null &&
                    numberVal !== undefined &&
                    numberVal >= 0
                ) {
                    return false;
                } else {
                    if (isNaN(numberVal) || numberVal === undefined || numberVal === null) {
                        return true;
                    } else {
                        return false;
                    }
                }
            } else if (field === FieldNames.featureFields.estimatedTotalCost) {
                if (numberVal > 0) {
                    return false;
                } else {
                    return true;
                }
            } else {
                if (isNaN(numberVal) || !numberVal) {
                    return true;
                } else {
                    return false;
                }
            }
        } else {
            return true;
        }
    };

    const showNotSavedClasses = (field) => {
        var fieldState = false;
        if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.treatment
        ) {
            // Estimated Treatment
            if (treatmentValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = treatmentValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualTreatment
        ) {
            // Actual Treatment
            if (actualsTreatmentValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = actualsTreatmentValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.activity
        ) {
            // Estimated Activity
            if (activityValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = activityValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualActivity
        ) {
            // Actual Activity
            if (actualsActivityValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = actualsActivityValidationUnsavedValues[field];
            return fieldState;
        } else {
            return false;
        }
    };

    return (
        <FormControl variant='standard' fullWidth>
            <ThemeProvider theme={theme}>
                <Input
                    id='outlined-adornment-amount'
                    value={value}
                    disabled={
                        field === FieldNames.featureFields.estimatedTotalCost ||
                        field === FieldNames.featureFields.estimatedAssetCost ||
                        field === FieldNames.featureFields.estimatedPersonnelCost ||
                        field === FieldNames.featureFields.estimatedContractualCost ||
                        field === FieldNames.featureFields.estimatedOtherCost ||
                        field === FieldNames.featureFields.estimatedGrantsFixedCost ||
                        field === FieldNames.featureFields.contributedTotalCost
                            ? true
                            : getAllApprovalFieldsCount(detail, isRequired)
                    }
                    {...(isRequired && inputValueMissing(value)
                        ? { className: classes.underline }
                        : showNotSavedClasses(field)
                          ? { className: notSavedClasses.underline }
                          : { className: '' })}
                    variant='standard'
                    onChange={(event) => {
                        const decimalFreeValue = removeDecimal(event.target.value);
                        const negativeFreeValue = removeNegative(decimalFreeValue);
                        const letterFreeValue = removeLetters(negativeFreeValue);
                        const commasAdded = currencyFormat(letterFreeValue);
                        setValue(commasAdded);
                        onFieldChange(field, commasAdded);
                    }}
                    startAdornment={
                        <InputAdornment style={{ marginTop: getMargin() }} position='start'>
                            $
                        </InputAdornment>
                    }
                />
            </ThemeProvider>
        </FormControl>
    );
}

DetailsCurrency.propTypes = {
    detail: PropTypes.any,
    currencyValue: PropTypes.any,
    field: PropTypes.string.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    isRequired: PropTypes.bool.isRequired,
    getAllApprovalFieldsCount: PropTypes.func.isRequired,
    costFields: PropTypes.array,
};
